// Tree columns and node pickers pin their selected node above the list when that
// node is not on the first loaded page — instead of paging through the (possibly
// 1000+ rows) list until it shows up. The decision is latched per query +
// selection: "Show more"/infinite scroll and cache-and-network refetches must not
// flip it (the pin would vanish and the list jump), only a new selection, parent,
// search or filter decides anew. A click selects a loaded node, so it never pins.

export interface PinnedSelectionDecision {
    key: string;
    pinned: boolean;
}

/**
 * @param previous   the decision of the last render
 * @param queryKey   identifies parent + search + filter of the column
 * @param loadedNodeIds ids of the rows loaded for `queryKey`; `undefined` while
 *                   the first page for it has not arrived from the network yet
 *                   (a cache hit may still contain pages a refetch drops)
 * @returns the latched decision, or `null` while there is nothing to decide yet
 */
export const decidePinnedSelection = (
    previous: PinnedSelectionDecision | null,
    queryKey: string,
    selectedNodeId: string | null | undefined,
    loadedNodeIds: string[] | undefined
): PinnedSelectionDecision | null => {
    if (!selectedNodeId) return null;
    const key = `${queryKey}|${selectedNodeId}`;
    if (previous?.key === key) return previous;
    if (!loadedNodeIds) return null;
    return { key, pinned: !loadedNodeIds.includes(selectedNodeId) };
};
